import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../services/api';
import type { ExecutionSummary } from '../../../types/agent-studio';
import { AgentStatusPill } from '../../../components/agent-studio';

export interface ExecutionsTabProps {
  systemName: string;
}

const INVOCATION_LABELS: Record<string, string> = {
  pipeline:        'Пайплайн',
  studio_test:     'Студия',
  task:            'Задача',
  eval_harness:    'Оценка',
  workspace_query: 'Рабочее пространство',
};

function formatRelativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMin = Math.floor((now - then) / 60000);
  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} ч назад`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD} дн назад`;
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function LoadingRows() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {[0, 1, 2, 3, 4].map(i => (
        <div key={i} className="card" style={{ height: '52px', opacity: 0.4 }} />
      ))}
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="card" style={{ padding: '24px', borderLeft: '3px solid var(--red)' }}>
      <div className="t-eyebrow" style={{ color: 'var(--red)' }}>Ошибка загрузки</div>
      <p className="t-body-sm" style={{ marginTop: '8px', color: 'var(--ink-secondary)' }}>{message}</p>
    </div>
  );
}

function EmptyCard({ message }: { message: string }) {
  return (
    <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
      <p className="t-body" style={{ color: 'var(--ink-secondary)' }}>{message}</p>
    </div>
  );
}

function FilterSelect<T extends string>({ label, value, onChange, options }: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div>
      <div className="t-eyebrow" style={{ fontSize: '11px', marginBottom: '4px', color: 'var(--ink-secondary)' }}>
        {label}
      </div>
      <select
        value={value}
        onChange={e => onChange(e.target.value as T)}
        style={{
          padding: '6px 10px',
          fontSize: '13px',
          border: '1px solid var(--border-subtle, rgba(0,0,0,0.12))',
          borderRadius: '4px',
          background: 'white',
          cursor: 'pointer',
        }}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

function ExecutionRow({ execution, onClick }: {
  execution: ExecutionSummary;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className="card"
      style={{
        padding: '14px 20px',
        marginBottom: '8px',
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: '90px 1fr auto auto auto auto auto',
        gap: '20px',
        alignItems: 'center',
        transition: 'border-color 0.15s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--navy)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = ''; }}
    >
      <AgentStatusPill status={execution.status} />
      <div style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--ink-secondary)' }}>
        #{execution.id.slice(-8)}
      </div>
      <div style={{ fontSize: '13px', color: 'var(--ink-secondary)' }}>
        {formatRelativeTime(execution.started_at)}
      </div>
      <div style={{ fontSize: '13px', minWidth: '60px', textAlign: 'right' }}>
        {execution.latency_seconds != null ? `${execution.latency_seconds.toFixed(1)}с` : '—'}
      </div>
      <div style={{ fontSize: '13px', minWidth: '60px', textAlign: 'right' }}>
        {execution.cost_usd != null ? `$${execution.cost_usd.toFixed(4)}` : '—'}
      </div>
      <div style={{ fontSize: '12px', color: 'var(--ink-secondary)' }}>
        {INVOCATION_LABELS[execution.invocation_source] ?? execution.invocation_source}
      </div>
      <div style={{ color: 'var(--ink-tertiary)' }}>›</div>
    </div>
  );
}

export function ExecutionsTab({ systemName }: ExecutionsTabProps) {
  const navigate = useNavigate();
  const [executions, setExecutions] = useState<ExecutionSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<'all' | 'running' | 'completed' | 'failed'>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'pipeline' | 'studio_test' | 'task' | 'eval_harness' | 'workspace_query'>('all');

  async function fetchInitial() {
    try {
      setLoading(true);
      setError(null);
      const response = await api.listAgentExecutions(systemName, { limit: 20 });
      setExecutions(response.executions);
      setNextCursor(response.next_cursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function fetchMore() {
    if (!nextCursor) return;
    try {
      setLoadingMore(true);
      const response = await api.listAgentExecutions(systemName, { limit: 20, cursor: nextCursor });
      setExecutions(prev => [...prev, ...response.executions]);
      setNextCursor(response.next_cursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    fetchInitial();
  }, [systemName]);

  const filteredExecutions = executions.filter(e => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (sourceFilter !== 'all' && e.invocation_source !== sourceFilter) return false;
    return true;
  });

  return (
    <div>
      <div style={{
        display: 'flex',
        gap: '24px',
        marginBottom: '20px',
        padding: '16px 20px',
        background: 'var(--bg-subtle, rgba(0,0,0,0.03))',
        borderRadius: '4px',
      }}>
        <FilterSelect
          label="Статус"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all',       label: 'Все' },
            { value: 'running',   label: 'Выполняется' },
            { value: 'completed', label: 'Завершён' },
            { value: 'failed',    label: 'Ошибка' },
          ]}
        />
        <FilterSelect
          label="Источник"
          value={sourceFilter}
          onChange={setSourceFilter}
          options={[
            { value: 'all',          label: 'Все' },
            { value: 'pipeline',        label: 'Пайплайн' },
            { value: 'studio_test',     label: 'Студия' },
            { value: 'task',            label: 'Задача' },
            { value: 'eval_harness',    label: 'Оценка' },
            { value: 'workspace_query', label: 'Рабочее пространство' },
          ]}
        />
        <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: '13px', color: 'var(--ink-secondary)' }}>
          Показано: {filteredExecutions.length} из {executions.length} загруженных
        </div>
      </div>

      {loading && <LoadingRows />}
      {!loading && error && <ErrorCard message={error} />}
      {!loading && !error && filteredExecutions.length === 0 && executions.length === 0 && (
        <EmptyCard message="Запусков пока не было." />
      )}
      {!loading && !error && filteredExecutions.length === 0 && executions.length > 0 && (
        <EmptyCard message="Под заданные фильтры запусков нет." />
      )}
      {!loading && !error && filteredExecutions.length > 0 && (
        <div>
          {filteredExecutions.map(exec => (
            <ExecutionRow
              key={exec.id}
              execution={exec}
              onClick={() => navigate(`/agents/${systemName}/executions/${exec.id}`)}
            />
          ))}
          {nextCursor && (
            <button
              onClick={fetchMore}
              disabled={loadingMore}
              style={{
                width: '100%',
                marginTop: '16px',
                padding: '12px',
                background: 'transparent',
                border: '1px solid var(--border-subtle, rgba(0,0,0,0.12))',
                borderRadius: '4px',
                cursor: loadingMore ? 'wait' : 'pointer',
                fontSize: '14px',
                color: 'var(--ink-secondary)',
              }}
            >
              {loadingMore ? 'Загрузка...' : 'Загрузить ещё'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
